<template lang="pug">
ValidationObserver(ref="dialogObserver")
  AppDialog.vue-time-entries-delete-multiple-dialog(
    title="Delete multiple time entries"
    append-to-body
    :show="show"
    @close="close"
  )
    template(slot="corner")
      a.dialog-faq-link(
        target="_blank"
        title="Read the help article about this feature"
        rel="noopener noreferrer"
        href="https://support.hubstaff.com/how-to-add-delete-and-edit-manual-time-entries/"
      ) ?
    form.simple_form.time_entry_form.form-horizontal
      .row
      .row.form-group
        label.required.control-label.col-sm-3
          abbr(title="required")
            | Time entries
        .controls.col-sm-9
          .review
            | {{ timeEntriesCount }} {{ timeEntriesCount | pluralize('time entry', 'time entries') }}
      .row.form-group(v-if="projectsCount > 0")
        label.required.control-label.col-sm-3
          abbr(title="required")
            | Projects
        .col-sm-9
          .review
            | {{ projectsCount }} {{ projectsCount | pluralize('project', 'projects') }}
      .row.form-group(v-if="projectsCount > 0")
        label.required.control-label.col-sm-3
          abbr(title="required")
            | To-dos
        .controls.col-sm-9
          .review
            | {{ tasksCount }} {{ tasksCount | pluralize('task', 'tasks') }}
      .row.form-group(v-if="workOrdersCount > 0")
        label.required.control-label.col-sm-3
          abbr(title="required")
            | Work orders
        .col-sm-9
          .review
            | {{ workOrdersCount }} {{ workOrdersCount | pluralize('work order', 'work orders') }}
      .row.form-group(v-if="workOrdersCount > 0")
        label.required.control-label.col-sm-3
          abbr(title="required")
            | Jobs
        .controls.col-sm-9
          .review
            | {{ jobsCount }} {{ jobsCount | pluralize('job', 'jobs') }}
      .row.form-group
        label.required.control-label.col-sm-3
          abbr(title="required")
            | Time
        .controls.col-sm-9
          .review
            | {{ sumDuration(timeEntries) | formatDuration }}
      .form-group
        label.col-sm-3.control-label
        .col-sm-9.mr-15.ml-15
          TimeEntriesTextarea(
            v-model="note"
            name="note"
            label="Reason"
            placeholder="Why are you deleting these time entries? (ex: Forgot to stop timer)"
            :required="requireReason"
          )

    template(slot="footer")
      button.btn.btn-default(@click="close") Cancel
      button.btn.btn-danger(
        type="submit"
        :disabled="saving"
        @click="save"
      )
        | {{ saveButtonLabel }}
  TimeEntriesOverrideDialog(
    v-if="showOverrideDialog"
    multiple
    type="delete"
    :show.sync="showOverrideDialog"
    :affected-records="affectedRecords"
    @submit="save({ confirmed: true })"
    @close="handleOverrideCancel"
  )
</template>

<script>
import { mapGetters } from 'vuex';
import { uniqBy, filter, map, some, includes } from 'lodash-es';
import { TimeEntryOverrideApprovedTimesheet } from '~/mixins';

export default {
  name: 'TimeEntriesDeleteMultipleDialog',
  mixins: [TimeEntryOverrideApprovedTimesheet],
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    timeEntries: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      saving: false,
      valid: false,
      showOverrideDialog: false,
      affectedRecords: {},
      note: '',
      errorMessage: 'There was an error when deleting these time entries',
      successMessage: 'Time entries deleted',
    };
  },
  computed: {
    ...mapGetters(['user', 'timeZoneList', 'getProject', 'sumDuration', 'timeEntriesToBatchDelete']),
    timeEntriesCount() {
      return this.timeEntries.length;
    },
    projects() {
      return uniqBy(filter(this.timeEntries, this.isProject), 'project_id');
    },
    workOrders() {
      return uniqBy(filter(this.timeEntries, this.isWorkOrder), 'project_id');
    },
    projectsCount() {
      return this.projects.length;
    },
    tasksCount() {
      const timeEntries = filter(
        this.timeEntries,
        (te) => Number.isInteger(te.task_id) && includes(this.projects, te.project_id),
      );

      return uniqBy(timeEntries, 'task_id').length;
    },
    workOrdersCount() {
      const projEntries = filter(this.timeEntries, this.isWorkOrder);

      return uniqBy(projEntries, 'project_id').length;
    },
    jobsCount() {
      const timeEntries = filter(
        this.timeEntries,
        (te) => Number.isInteger(te.task_id) && includes(this.workOrders, te.project_id),
      );

      return uniqBy(timeEntries, 'task_id').length;
    },
    saveButtonLabel() {
      return this.saving ? 'Deleting...' : 'Delete';
    },
    requireReason() {
      const projects = this.timeEntriesToBatchDelete.map((timeEntry) => this.getProject(timeEntry.project_id));

      return some(projects, 'reason_required');
    },
    timeEntryParams() {
      return {
        user_id: this.user.id,
        time_zone: this.timeZoneList.time_zone_id,
        needs_html: false,
        time_entry: {
          note: this.note,
        },
      };
    },
  },
  watch: {
    show: function (value) {
      if (value) {
        this.reset();
      }
    },
  },
  methods: {
    async save({ confirmed = false } = {}) {
      const isValid = await this.$refs.dialogObserver.validate();

      if (isValid) {
        const success = this.success;
        const error = () => this.$appMessage(this.errorMessage);
        const fin = () => {
          this.saving = false;
        };

        this.saving = true;
        const params = {
          ids: map(this.timeEntries, 'id'),
          time_entry: { note: this.note },
          confirmed,
        };

        await this.$store.dispatch('deleteTimeEntries', { params }).then(success, error).then(fin);
      }
    },
    async success(response) {
      if (response.action === 'confirm-override') {
        this.affectedRecords = response.associated_records;
        this.timesheetRedirectUrl = response.timesheet_redirect_url;
        this.showOverrideDialog = true;
      } else if (response.action === 'error-response') {
        const errors = response.errors;

        this.$refs.dialogObserver.setErrors(errors);
        this.$appMessage(response.alert);
      } else {
        this.$appMessage(this.successMessage);

        await this.handleSuccessfulAction();
      }
    },
    reset() {
      this.note = '';
      this.timesheetRedirectUrl = null;
      if (this.$refs.dialogObserver) {
        this.$refs.dialogObserver.reset();
      }
    },
    close() {
      this.reset();
      this.$emit('close');
    },
    isWorkOrder(timeEntry) {
      return this.getProject(timeEntry.project_id).work_order;
    },
    isProject(timeEntry) {
      return !this.isWorkOrder(timeEntry);
    },
  },
};
</script>
